import type { LegalDocument } from './types'

export const researchAndInsightsConsent: LegalDocument = {
  path: '/research-and-insights-consent',
  title: 'Research and Insights Consent',
  blocks: [
    {
      type: 'paragraph',
      text: 'Optional · Separate from your engagement · Withdrawable at any time',
    },
    {
      type: 'paragraph',
      text: 'MissFit Coaching™ is building research on how senior professionals navigate career transitions — what triggers them, which channels produce offers, and how searches actually unfold. Reliable data of this kind barely exists. Your search generates real information, and this form asks your permission to let some of it count.',
    },
    {
      type: 'paragraph',
      text: [
        { text: 'This changes nothing about your coaching, whatever you decide.', bold: true },
        {
          text: ' Not the scope, the pace, the price, or how MissFit Coaching™ shows up for you. There is no benefit to consenting and no cost to declining.',
        },
      ],
    },

    { type: 'heading', text: 'WHAT CONSENT COVERS' },
    {
      type: 'paragraph',
      text: 'Approving this form authorizes three uses, and only these three:',
    },
    {
      type: 'numbered',
      items: [
        [
          { text: 'Combined statistics.', bold: true },
          {
            text: ' Your search facts — level, industry, time to offer, channels used, compensation change — folded into group numbers alongside many other clients. Never reported as an individual record.',
          },
        ],
        [
          { text: 'Coded themes.', bold: true },
          {
            text: ' Patterns from our conversations, such as what triggered your transition, turned into categories like "34% of transitions followed a reporting-line change." Your specific story is never described.',
          },
        ],
        [
          { text: 'Anonymized quotes, reviewed by you first.', bold: true },
          {
            text: ' Short excerpts of what you said, with your name and employer removed, used in reports and presentations. You will always receive the exact excerpt by email before publication, and it will not be used until you approve it. If there is no reply within 14 days, the excerpt is not used. Silence never publishes your words.',
          },
        ],
      ],
    },

    { type: 'heading', text: 'WHAT CONSENT DOES NOT COVER' },
    {
      type: 'callout',
      text: 'The following require their own separate and explicit authorization. Neither is included in a general approval.',
    },
    {
      type: 'bullets',
      items: [
        [
          { text: 'Press and media.', bold: true },
          {
            text: ' Providing anything to journalists or media outlets is a separate decision, because once a reporter publishes, neither party can retract it. To include this, name it explicitly in your reply.',
          },
        ],
        [
          { text: 'Licensed datasets.', bold: true },
          {
            text: ' Including your fully anonymized information in a dataset MissFit Coaching™ may license to a research partner is likewise separate. Name it explicitly, or it is excluded.',
          },
        ],
      ],
    },

    { type: 'heading', text: 'WHAT IS NEVER INCLUDED' },
    {
      type: 'paragraph',
      text: 'Regardless of what you authorize: anything from your recruiter or employer correspondence, and anything predating the date of your election, unless you specifically direct otherwise.',
    },

    { type: 'heading', text: 'HOW YOUR IDENTITY IS PROTECTED' },
    {
      type: 'paragraph',
      text: 'Before any use, your name, employer, and identifying details are removed. MissFit Coaching™ maintains one restricted-access record linking your consent to your file, so that the permissions granted can be evidenced and any withdrawal honored. That record is never published, shared, or transferred.',
    },

    { type: 'heading', text: 'WITHDRAWAL' },
    {
      type: 'paragraph',
      text: 'You may withdraw or change this consent at any time, in writing, and it will be applied within 30 days. Withdrawal cannot undo material already published, or already provided to media or a licensee, before the withdrawal was received. Nothing further is released after that point.',
    },

    { type: 'heading', text: 'HOW TO RESPOND' },
    {
      type: 'callout',
      text: [
        { text: '"Approved"', bold: true },
        { text: '\nAuthorizes the three uses above, and nothing else.' },
      ],
    },
    {
      type: 'callout',
      text: [
        { text: '"Not interested," or no reply', bold: true },
        {
          text: '\nNothing is used, ever. No explanation is needed. If you would prefer to decide later, say so and the question stays open; silence is not treated as a final decision.',
        },
      ],
    },
    {
      type: 'callout',
      text: [
        { text: 'Anything else', bold: true },
        {
          text: '\nState what you want changed — adding press, adding licensing, removing one of the three, or any combination. Your revised terms will be confirmed back to you in writing before anything is used.',
        },
      ],
    },
    {
      type: 'paragraph',
      text: 'Reply to the email carrying this form. Your emailed reply is the record of your election.',
    },
    {
      type: 'paragraph',
      text: 'MissFit Research and Insights Consent · Version 1.1 · Effective September 11, 2026',
    },
    {
      type: 'paragraph',
      text: 'Dee Dee Holdings LLC (d/b/a MissFit Coaching™) · dee@missfitcoaching.com',
    },
  ],
}
